import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { take } from 'rxjs';
import { licenceActions } from '../../actions/licence.actions';
import { CreateOrUpdateLicencePackageDto } from '../../dtos/CreateLicencePackageDto.interface';

import { LicenceProvider } from '../../provider/licence.provider';
import { CreateOrUpdateLicencePackageComponent } from '../licence-package/create-licence-package.component';

@UntilDestroy()
@Component({
    selector: 'show-organization-packages',
    templateUrl: './show-organization-packages.component.html',
    styleUrls: ['./show-organization-packages.component.scss'],
})
export class ShowOrganizationPackagesComponent implements OnInit {
    showDeleteModal: boolean;
    packageIdToDelete: string;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: { packages: CreateOrUpdateLicencePackageDto[]; organizationId: string; organizationName: string },
        public dialog: MatDialog,
        private readonly licenceProvider: LicenceProvider,
        private readonly actionsListener$: ActionsSubject,
    ) {}

    ngOnInit(): void {
        this.actionsListener$
            .pipe(ofType(licenceActions.createLicenceSuccess), untilDestroyed(this))
            .subscribe((response) => {
                if (!this.data.packages) {
                    this.data.packages = [];
                }

                if (this.data.organizationId === response.payload.organizationId) {
                    const index = this.data.packages.findIndex((p) => p.packageId === response.payload.packageId);
                    if (index === -1) {
                        this.data.packages = [...this.data.packages, response.payload];
                    }
                }
            });

        this.actionsListener$
            .pipe(ofType(licenceActions.updateLicenceSuccess), untilDestroyed(this))
            .subscribe((response) => {
                const updatedPackage = response.payload;
                const index = this.data.packages.findIndex((p) => p.packageId === updatedPackage.packageId);
                this.data.packages[index] = updatedPackage;
                this.data.packages = [...this.data.packages];
            });

        this.actionsListener$
            .pipe(ofType(licenceActions.deleteLicenceSuccess), untilDestroyed(this))
            .subscribe((response) => {
                const packageId = response.packageId;
                const index = this.data.packages.findIndex((p) => p.packageId === packageId);

                this.data.packages.splice(index, 1);
                this.data.packages = [...this.data.packages];
            });
    }

    onAddPackage() {
        this.openCreateEditPackageDialog();
    }

    onEditPackage(packageToEdit: CreateOrUpdateLicencePackageDto) {
        this.openCreateEditPackageDialog(packageToEdit);
    }

    onDeletePackage(packageId: string) {
        this.showDeleteModal = true;
        this.packageIdToDelete = packageId;
    }

    onConfirmDelete() {
        const { organizationId } = this.data;
        this.licenceProvider.dispatchDeleteLicencePackage(this.packageIdToDelete, organizationId);
        this.showDeleteModal = false;
    }

    private openCreateEditPackageDialog(packageToEdit?: CreateOrUpdateLicencePackageDto) {
        const { organizationId } = this.data;
        const dialogRef = this.dialog.open(CreateOrUpdateLicencePackageComponent, {
            width: '600px', // Adjust the width as needed
            data: { organizationId: organizationId, package: packageToEdit },
        });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((createOrUpdateLicencePackageDto: CreateOrUpdateLicencePackageDto | undefined) => {
                if (createOrUpdateLicencePackageDto && !packageToEdit) {
                    // Ensure organizationId is present
                    if (!createOrUpdateLicencePackageDto.organizationId) {
                        createOrUpdateLicencePackageDto.organizationId = organizationId;
                    }

                    this.licenceProvider.dispatchAddPackage(organizationId, createOrUpdateLicencePackageDto);
                } else if (createOrUpdateLicencePackageDto && packageToEdit) {
                    createOrUpdateLicencePackageDto.packageId = packageToEdit.packageId;
                    this.licenceProvider.dispatchUpdatePackage(createOrUpdateLicencePackageDto);
                }
            });
    }
}
